<template>
  <div class="t-app__container">
    <heading :title="$t('edit-my-information')" />
    <div class="h-section h-section--no-margin-top">
      <div class="h-grid h-grid--justify-content-center">
        <div class="h-grid__col h-grid__col--sm-10 h-grid__col--md-8">
          <template v-if="updated">
            <p v-html="$t('edit-informations-success')"></p>
            <button class="a-btn-primary-outline" type="button" @click="returnHome">{{ $t('return-btn') }}</button>
          </template>
          <template v-else>
            <s-form ref="form" @submit="editMyInformation">
              <h2>{{ $t('phone-number') }}</h2>
              <p class="m-form-group">
                <label class="a-form-label" for="phoneNumber">{{ $t('label-phone-number') }}</label>
                <input class="a-form-control" id="phoneNumber" v-model.lazy="$v.phoneNumber.$model" autocomplete="phone-number" />
                <span v-if="$v.phoneNumber.$dirty">
                  <span class="m-form-group__error" v-if="!$v.phoneNumber.required">{{ $t('error-field-required') }}</span>
                  <span class="m-form-group__error" v-if="!$v.phoneNumber.isPhone">{{ $t('error-phone-field-format') }}</span>
                </span>
              </p>

              <h2>{{ $t('password') }}</h2>
              <p class="a-label-accent">{{ $t('edit-my-information.notice-for-keep-same-password') }}</p>
              <p class="m-form-group">
                <label for="password" class="a-form-label" :value="password">
                  {{ $t('current-password') }}
                </label>
                <input v-model.trim="$v.password.$model" type="password" id="password" class="a-form-control" />
                <span v-if="$v.password.$dirty">
                  <span class="m-form-group__error" v-if="!$v.password.required">{{ $t('error-field-required') }}</span>
                  <span class="m-form-group__error" v-if="!$v.password.isValidPassword">
                    {{ $t('error-create-password-invalid') }}
                  </span>
                </span>
              </p>
              <p class="m-form-group">
                <label for="newPassword" class="a-form-label" :value="newPassword">
                  {{ $t('new-password') }}
                  <span class="m-form-group__note m-form-group__note--small"
                    >{{ $t('user-password-rules') }}
                    <ul>
                      <li>{{ $t('user-password-rule-1') }}</li>
                      <li>{{ $t('user-password-rule-2') }}</li>
                      <li>{{ $t('user-password-rule-3') }}</li>
                    </ul>
                  </span>
                </label>
                <input v-model.trim="$v.newPassword.$model" type="password" id="newPassword" class="a-form-control" />
                <span v-if="$v.newPassword.$dirty">
                  <span class="m-form-group__error" v-if="!$v.newPassword.isValidPassword">
                    {{ $t('error-create-password-invalid') }}
                  </span>
                </span>
              </p>
              <p class="m-form-group">
                <label for="passwordConfirmation" class="a-form-label" :value="passwordConfirmation">{{ $t('new-password-confirmation-label') }}</label>
                <input v-model.trim="$v.passwordConfirmation.$model" type="password" id="passwordConfirmation" class="a-form-control" />
                <span v-if="$v.passwordConfirmation.$dirty">
                  <span class="m-form-group__error" v-if="!$v.passwordConfirmation.required">{{ $t('error-field-required') }}</span>
                  <span class="m-form-group__error" v-if="!$v.passwordConfirmation.isSamePassword">
                    {{ $t('error-password-confirmation-invalid') }}
                  </span>
                </span>
              </p>
              <div class="m-form-ctrl">
                <div class="m-form-ctrl__item">
                  <button class="a-btn-primary" type="submit">
                    {{ $t('reset-password-submit-btn') }}
                  </button>
                </div>
                <div class="m-form-ctrl__item">
                  <router-link :to="{ name: $consts.urls.URL_USER_PARTICIPATE_DASHBOARD }">
                    {{ $t('return-btn') }}
                  </router-link>
                </div>
              </div>
            </s-form>
          </template>
        </div>
      </div>
    </div>

    <div class="t-app__confetti t-app__confetti--01"></div>
    <div class="t-app__dot t-app__dot--large"></div>
    <div class="t-app__confetti t-app__confetti--02"></div>
    <div class="t-app__confetti t-app__confetti--03"></div>
    <div class="t-app__confetti t-app__confetti--04"></div>
    <div class="t-app__dot t-app__dot--medium"></div>
    <div class="t-app__confetti t-app__confetti--05"></div>
  </div>
</template>

<script>
import { required, requiredIf } from 'vuelidate/lib/validators'

import Heading from '@/components/heading'
import SForm from '@/components/form/form'

import UserService from '@/services/user'
import Notification from '@/services/notification'
import LoaderService from '@/services/loader'

import { URL_USER_PARTICIPATE_DASHBOARD } from '@/consts/urls'

import FormHelper from '@/helpers/form'
import i18n from '@/helpers/i18n'

const isValidPassword = function(value) {
  return this.newPassword ? FormHelper.isValidPassword(value) : true
}
const isSamePassword = function(value) {
  return this.newPassword === value
}
const isPhone = value => FormHelper.isPhone(value)

export default {
  data() {
    return {
      token: this.$router.currentRoute.query.token,
      email: this.$router.currentRoute.query.email,
      phoneNumber: '',
      password: '',
      newPassword: '',
      passwordConfirmation: '',
      updated: false
    }
  },
  components: {
    SForm,
    Heading
  },
  validations: {
    phoneNumber: {
      required,
      isPhone
    },
    password: {
      required: requiredIf(model => {
        return Boolean(model.newPassword)
      }),
      isValidPassword
    },
    newPassword: {
      isValidPassword
    },
    passwordConfirmation: {
      required: requiredIf(model => {
        return Boolean(model.newPassword)
      }),
      isSamePassword
    }
  },
  async beforeRouteEnter(to, from, next) {
    LoaderService.showLoader()
    const user = await UserService.getMyInformations()
    LoaderService.hideLoader()

    next(vm => {
      vm.phoneNumber = user.PhoneNumber ? user.PhoneNumber.replace(/^(\d{3})(\d{3})(\d{4})$/g, '$1 $2-$3') : ''
    })
  },
  methods: {
    editMyInformation: async function() {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        LoaderService.showLoader()
        try {
          let result = await UserService.editMyInformation(this.phoneNumber, this.password, this.newPassword)
          if (result.status === 500) {
            Notification.showError(i18n.t('invalid-password'))
          } else {
            this.updated = true
            UserService.setHasPhoneNumber(true)
          }
        } finally {
          LoaderService.hideLoader()
        }
      }
    },
    returnHome: function() {
      this.$router.push({
        name: URL_USER_PARTICIPATE_DASHBOARD
      })
    }
  }
}
</script>
